import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { catchError, tap } from 'rxjs/operators';
import { forkJoin, of, throwError } from 'rxjs';

import { SessionState } from '@casb/session';
import { AuditNotificationsActions } from './audit-notifications.actions';
import {
  AuditNotificationInstance,
  AuditNotificationPatchResponse,
  AuditNotificationsCatalogResponse,
  AuditNotificationsSummaryResponse,
  AuditNotificationTableRow,
  NotificationRowDefinition,
  auditNotificationDisplayName,
} from '../src/audit-notifications.model';
import { NotificationSetting } from '../../notifications/src/notifications.model';
import {
  ColumnConfig,
  NAME_COLUMN,
  CHANNEL_COLUMN_WIDTH,
} from '../../notifications/src/notifications-table.config';

const CATALOG_PATH = 'api/admin/v1/audit/notification_settings/catalog/';
const SUMMARY_PATH = 'api/admin/v1/audit/notification_settings/summary/';
const instancePath = (tenantId: string) => `api/admin/v1/audit/notification_settings/${tenantId}/`;

export interface AuditNotificationsStateModel {
  loading: boolean;
  savePending: boolean;
  rowDefinitions: NotificationRowDefinition[];
  instance: AuditNotificationInstance | null;
}

@State<AuditNotificationsStateModel>({
  name: 'auditNotifications',
  defaults: {
    loading: false,
    savePending: false,
    rowDefinitions: [],
    instance: null,
  },
})
@Injectable()
export class AuditNotificationsState {
  private readonly httpClient = inject(HttpClient);
  private readonly store = inject(Store);

  private tenantUrl(path: string): string {
    const session = this.store.selectSnapshot(SessionState.getSession);
    const tenantId = session?.getTenantId() ?? '';
    return `/${tenantId}/${path}`;
  }

  @Selector()
  static loading(state: AuditNotificationsStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static savePending(state: AuditNotificationsStateModel): boolean {
    return state.savePending;
  }

  @Selector()
  static rowDefinitions(state: AuditNotificationsStateModel): NotificationRowDefinition[] {
    return state.rowDefinitions;
  }

  @Selector()
  static instance(state: AuditNotificationsStateModel): AuditNotificationInstance | null {
    return state.instance;
  }

  /** Same column-derivation shape as SpanVA's NotificationsState.columns — Name + one per channel. */
  @Selector()
  static columns(state: AuditNotificationsStateModel): ColumnConfig[] {
    const rowColumns: ColumnConfig[] = state.rowDefinitions.map((row) => ({
      id: row.ui_id,
      header: row.title,
      field: row.ui_id,
      filtername: row.ui_id,
      resizable: true,
      sortable: true,
      visible: true,
      width: CHANNEL_COLUMN_WIDTH,
      is_default: true,
    }));
    return [NAME_COLUMN, ...rowColumns];
  }

  /** Audit has exactly one settings document per tenant, so this is a single-row table. */
  @Selector()
  static tableRows(state: AuditNotificationsStateModel): AuditNotificationTableRow[] {
    const instance = state.instance;
    if (!instance) {
      return [];
    }
    const channelsByUiId: AuditNotificationTableRow['channelsByUiId'] = {};
    for (const def of state.rowDefinitions) {
      const key = def.channel_key ?? def.channel_keys?.[0];
      channelsByUiId[def.ui_id] = key ? instance.channels[key] : undefined;
    }
    return [
      {
        tenant_id: instance.tenant_id,
        name: auditNotificationDisplayName(instance.tenant_name),
        master_enabled: instance.master_enabled,
        recipients: instance.recipients ?? [],
        channelsByUiId,
      },
    ];
  }

  @Action(AuditNotificationsActions.LoadSummary)
  loadSummary(ctx: StateContext<AuditNotificationsStateModel>) {
    ctx.patchState({ loading: true });

    return forkJoin({
      catalog: this.httpClient
        .get<AuditNotificationsCatalogResponse>(this.tenantUrl(CATALOG_PATH))
        .pipe(
          catchError((err) => {
            console.error('Audit notification settings: catalog/ request failed', err);
            return of(null);
          }),
        ),
      summary: this.httpClient
        .get<AuditNotificationsSummaryResponse>(this.tenantUrl(SUMMARY_PATH))
        .pipe(
          catchError((err) => {
            console.error('Audit notification settings: summary/ request failed', err);
            return of(null);
          }),
        ),
    }).pipe(
      tap(({ catalog, summary }) => {
        const anyFailed = catalog === null || summary === null;
        const rowDefinitions = anyFailed
          ? []
          : (catalog?.notification_rows ?? summary?.notification_rows ?? []);
        const instance = anyFailed ? null : (summary?.instances?.[0] ?? null);

        ctx.patchState({ loading: false, rowDefinitions, instance });

        if (anyFailed) {
          throw new Error('Failed to load notification settings.');
        }
      }),
    );
  }

  @Action(AuditNotificationsActions.Save)
  save(ctx: StateContext<AuditNotificationsStateModel>, action: AuditNotificationsActions.Save) {
    const tenantId = ctx.getState().instance?.tenant_id;
    if (!tenantId) {
      return throwError(() => new Error('Notification settings have not loaded yet.'));
    }

    ctx.patchState({ savePending: true });

    return this.httpClient
      .patch<AuditNotificationPatchResponse>(this.tenantUrl(instancePath(tenantId)), action.payload)
      .pipe(
        tap((response) => {
          if (response.error) {
            throw new Error(response.error);
          }
          const updated = response as AuditNotificationInstance;
          ctx.patchState({
            instance: updated?.tenant_id ? updated : ctx.getState().instance,
            savePending: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({ savePending: false });
          return throwError(() => err);
        }),
      );
  }

  /**
   * Builds the ordered channels PATCH payload from the drawer's edited settings —
   * mirrors NotificationsState.buildPatchPayload, including frequency when enabled
   * (some Audit channels, e.g. datasource_no_data, are window-scheduled, not event-only).
   */
  static buildPatchPayload(
    rowDefs: NotificationRowDefinition[],
    settings: NotificationSetting[],
    recipients: string[],
    masterEnabled: boolean,
  ) {
    const channels: Record<string, { enabled: boolean; frequency?: string }> = {};
    for (const setting of settings) {
      const def = rowDefs.find((d) => d.ui_id === setting.ui_id);
      const key = def?.channel_key ?? def?.channel_keys?.[0];
      if (key) {
        channels[key] = setting.enabled
          ? { enabled: true, frequency: setting.frequency }
          : { enabled: false };
      }
    }
    return { enable_all: masterEnabled, recipients, channels };
  }
}
